/* RESPONSIBLE TEAM: team-workflows */
/* eslint-disable no-restricted-imports */
import {
  TRIGGERABLE_BOT_TYPE,
  CX_SCORE_RATING_RECEIVED_TARGET,
  CX_SCORE_RATING_RECEIVED_TRIGGER,
  DURING_CONVERSATION,
  WORKFLOWS_SETUP_TRIGGER_DURING_CONVERSATION,
  WORKFLOWS_SETUP_TRIGGER_INTERNAL_PROCESS,
} from 'embercom/lib/operator/custom-bots/constants';
import {
  matchBehaviors,
  objectTypes,
} from 'embercom/models/data/matching-system/matching-constants';
import CxScoreRatingReceivedEditorConfig from 'embercom/objects/visual-builder/configuration/editor/cx-score-rating-received';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

import CustomBotConfig, { type ColumnConfigs, type TriggerPredicates } from '../custom-bot';
import type { BotConfigTarget } from '../configuration';
import { TRIGGER_PREDICATE_PREFIX } from 'embercom/lib/workflows/constants/webhook_received';
import { getApp } from 'embercom/lib/container-lookup';

const ATTRIBUTE_PATH = `${TRIGGER_PREDICATE_PREFIX}.customer_experience_rating`;

export default class CxScoreRatingReceivedCustomBotConfig extends CustomBotConfig {
  visibleToApp(app: any) {
    return app.canSeeCxScore as boolean;
  }

  get target(): BotConfigTarget {
    return CX_SCORE_RATING_RECEIVED_TARGET;
  }

  get botType() {
    return TRIGGERABLE_BOT_TYPE;
  }

  get objectType() {
    return objectTypes.triggerableCustomBot;
  }

  get trigger() {
    return CX_SCORE_RATING_RECEIVED_TRIGGER;
  }

  get matchBehavior() {
    return matchBehaviors.transient;
  }

  get titleTranslationKey() {
    return 'operator.custom-bot.trigger.cx-score-rating-received.title';
  }

  get descriptionTranslationKey() {
    return 'operator.custom-bot.trigger.cx-score-rating-received.description';
  }

  get icon(): InterfaceIconName {
    return 'fin';
  }

  get editorConfig() {
    return new this.EditorConfigClass({
      targetChannels: this.customBot?.targetChannels,
      areStepPaywallsActive: this.areStepPaywallsActive,
      workflow: this.workflow,
    });
  }

  get EditorConfigClass() {
    return CxScoreRatingReceivedEditorConfig;
  }

  get triggerPredicates(): TriggerPredicates {
    return {
      type: 'or',
      options: [
        {
          value: '1',
          translationKey: 'operator.custom-bot.trigger.cx-score-rating-received.ratings.1',
          predicate: {
            attribute: ATTRIBUTE_PATH,
            type: 'integer',
            comparison: 'eq',
            value: '1',
          },
        },
        {
          value: '2',
          translationKey: 'operator.custom-bot.trigger.cx-score-rating-received.ratings.2',
          predicate: {
            attribute: ATTRIBUTE_PATH,
            type: 'integer',
            comparison: 'eq',
            value: '2',
          },
        },
        {
          value: '3',
          translationKey: 'operator.custom-bot.trigger.cx-score-rating-received.ratings.3',
          predicate: {
            attribute: ATTRIBUTE_PATH,
            type: 'integer',
            comparison: 'eq',
            value: '3',
          },
        },
        {
          value: '4',
          translationKey: 'operator.custom-bot.trigger.cx-score-rating-received.ratings.4',
          predicate: {
            attribute: ATTRIBUTE_PATH,
            type: 'integer',
            comparison: 'eq',
            value: '4',
          },
        },
        {
          value: '5',
          translationKey: 'operator.custom-bot.trigger.cx-score-rating-received.ratings.5',
          predicate: {
            attribute: ATTRIBUTE_PATH,
            type: 'integer',
            comparison: 'eq',
            value: '5',
          },
        },
      ],
    };
  }

  get columnKeys(): (keyof ColumnConfigs)[] {
    let baseColumns: (keyof ColumnConfigs)[] = super.columnKeys;
    let idx = 2;

    if (getApp().canUseFeature('team-workflows-workflow-type-column')) {
      idx++;
    }

    return [...baseColumns.slice(0, idx), 'channels', ...baseColumns.slice(idx)];
  }

  get requiredBillingFeature() {
    return 'workflows_core_billing_feature';
  }

  get triggerContextType() {
    return DURING_CONVERSATION;
  }

  get workflowsSetupTriggerType(): string[] {
    return [WORKFLOWS_SETUP_TRIGGER_DURING_CONVERSATION, WORKFLOWS_SETUP_TRIGGER_INTERNAL_PROCESS];
  }
}
